'use client';

import { pdfjs } from 'react-pdf';
import {
  useMemo,
  useEffect,
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import type {
  AnnotationColor,
  Mode,
  Dimensions,
  PreviewProviderProps,
} from './PreviewProvider.types';
import { usePrevious } from '@/_hooks/usePrevious';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { getAnnotations } from '@/_api/admin-annotations';
import 'react-pdf/dist/Page/TextLayer.css';
import { getExtensionOfFilename } from '@/_utils/files';
import {
  EngagementSignature,
  Question,
  Questionnaire,
} from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';
import { getQuestionnaire } from '@/_api/client-questionnaires';
import { adminAnswerQuestion } from '@/_api/admin-questionnaires';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getBlockedSections, getTriggeredSections } from '@/_utils/sections';
import { removeGivenQuestionType } from '@/_utils/questions';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { PreviewDialog } from './_components/PreviewDialog';
import type { Annotation } from '@/_types/documents';
import { TaxReturn } from '@/_types/models/taxReturns';
import { getDocumentsUploadedOutsideOfQuestionnaire } from '@/_api/integrations';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DEFAULT_DIMENSION = {
  width: 0,
  height: 0,
  scale: 1,
  zoom: 1,
};

const SUPPORTED_PREVIEW_EXTENSIONS: Array<string> = [
  'pdf',
  'xlsx',
  'xls',
  'jpg',
  'jpeg',
  'avif',
  'gif',
  'heic',
  'png',
];

const PreviewContext = createContext<PreviewProviderProps>({
  color: 'black',
  currentPage: 1,
  dimensions: DEFAULT_DIMENSION,
  goToNextIncompletePreviewSection: () => null,
  isPreviewQuestionnairePending: false,
  loadPreviewQuestionnaire: () => null,
  getDocumentsUploadedOutside: (_companyId: number, _from3MonthsAgo: boolean) =>
    Promise.resolve([]),
  mode: 'view',
  pageRotations: {},
  previewAnnotations: [],
  previewCompanyId: undefined,
  previewCompanyIdForAnnotations: undefined,
  previewDocumentStatus: null,
  previewExtractedDocumentId: null,
  previewFile: undefined,
  previewFileOriginalLink: undefined,
  previewQuestionnaire: undefined,
  previewQuestions: [],
  previewSection: undefined,
  previewSectionQuestions: [],
  previewSections: [],
  rotationDegrees: 0,
  selectedIds: new Set(),
  selectedPreviewQuestion: undefined,
  selectedPreviewSignatures: [],
  selectedTaxReturn: undefined,
  setCurrentPage: () => null,
  setSelectedTaxReturn: () => null,
  setColor: () => null,
  setDimensions: () => null,
  setMode: () => null,
  setPageRotations: () => null,
  setPreviewAnnotations: () => null,
  setPreviewCompanyIdForAnnotations: () => null,
  setPreviewDocumentCreatedAt: () => null,
  setPreviewDocumentStatus: () => null,
  setPreviewExtractedDocumentId: () => null,
  setPreviewFile: (_link: string) => null,
  setPreviewFileOriginalLink: () => null,
  setPreviewQuestionnaire: (_questionnaire: Questionnaire) => null,
  setRotationDegrees: () => null,
  setSelectedIds: () => null,
  setSelectedPreviewQuestion: () => null,
  setShowIncompletePreviewNavigation: () => null,
  showIncompletePreviewNavigation: false,
  updatePreviewParentQuestion: () => null,
  updatePreviewQuestion: () => null,
});

const PreviewProvider = ({ children }: { children: ReactNode }) => {
  const { addToast } = useContext(ToastContext);
  const { token, isAdvisor } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const { companyId, questionnaireId } = useParams();
  const sparams = useSearchParams();
  const sectionId = sparams.get('sectionId');
  const { push } = useRouter();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [previewCompanyIdForAnnotations, setPreviewCompanyIdForAnnotations] =
    useState<number | null | undefined>(undefined);
  const [mode, setMode] = useState<Mode>('view');
  const [dimensions, setDimensions] = useState<Dimensions>(DEFAULT_DIMENSION);
  const [color, setColor] = useState<AnnotationColor>('black');
  const [rotationDegrees, setRotationDegrees] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageRotations, setPageRotations] = useState<Record<number, number>>(
    {},
  );
  const [showIncompletePreviewNavigation, setShowIncompletePreviewNavigation] =
    useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<string | undefined>(undefined);
  const [previewFileOriginalLink, setPreviewFileOriginalLink] = useState<
    string | undefined
  >(undefined);
  const [previewExtractedDocumentId, setPreviewExtractedDocumentId] = useState<
    number | null | undefined
  >(undefined);
  const [previewDocumentCreatedAt, setPreviewDocumentCreatedAt] = useState<
    string | null | undefined
  >(null);
  const [previewDocumentStatus, setPreviewDocumentStatus] = useState<
    string | null | undefined
  >(null);
  const [selectedPreviewQuestion, setSelectedPreviewQuestion] = useState<
    Question | undefined
  >(undefined);
  const [previewQuestionnaire, setPreviewQuestionnaire] = useState<
    Questionnaire | undefined
  >(undefined);
  const [previewAnnotations, setPreviewAnnotations] = useState<
    Array<Annotation>
  >([]);

  const [selectedTaxReturn, setSelectedTaxReturn] = useState<
    TaxReturn | undefined
  >(undefined);

  const previousPreviewQuestionnaire: Questionnaire | undefined =
    usePrevious(previewQuestionnaire);

  const setPreviewFileOrDownload = (link?: string) => {
    if (!link) {
      setPreviewFile(undefined);
      setPreviewFileOriginalLink(undefined);
      setPreviewAnnotations([]);

      return;
    }

    const extension = getExtensionOfFilename(link);

    if (SUPPORTED_PREVIEW_EXTENSIONS.includes(extension)) {
      return setPreviewFile(link);
    }

    return globalThis.open(link, '_blank');
  };

  const loadPreviewQuestionnaire = useCallback(async () => {
    if (!token || !isAdvisor || !questionnaireId || !companyId) {
      return;
    }

    try {
      const questionnaire = await getQuestionnaire({
        token,
        questionnaireId: Number(questionnaireId),
        companyId: Number(companyId),
      });

      setPreviewQuestionnaire(questionnaire);
      return questionnaire;
    } catch (error) {
      return addToast('error', null, { error });
    }
  }, [addToast, isAdvisor, questionnaireId, companyId, token]);

  const getDocumentsUploadedOutside = useCallback(
    async (companyId: number, from3MonthsAgo: boolean) => {
      try {
        const response: any = await getDocumentsUploadedOutsideOfQuestionnaire({
          companyId,
          from3MonthsAgo,
          token,
        });

        return response;
      } catch (error) {
        addToast('error', null, { error });
        return [];
      }
    },
    [token, addToast],
  );

  const previewSection = useMemo(() => {
    const firstSection: Section | undefined =
      previewQuestionnaire?.sections?.[0];
    const section: Section | undefined = previewQuestionnaire?.sections?.find(
      (s: Section) => s.id === Number(sectionId),
    );

    return section || firstSection;
  }, [previewQuestionnaire?.sections, sectionId]);

  const previewSectionQuestions: Array<Question> = useMemo(
    () =>
      previewQuestionnaire?.questionnaire_questions?.filter(
        (q: Question) => q.section_id === previewSection?.id,
      ) || [],
    [previewQuestionnaire?.questionnaire_questions, previewSection?.id],
  );

  const previewQuestions: Array<Question> = useMemo(
    () => previewQuestionnaire?.questionnaire_questions || [],
    [previewQuestionnaire],
  );

  const previewSections: Array<Section> = useMemo(
    () => previewQuestionnaire?.sections || [],
    [previewQuestionnaire],
  );

  const previewCompanyId: number = useMemo(
    () => Number(companyId as string),
    [companyId],
  );

  // Honor firm preference: hide upload questions within sections when multiple sections exist
  const filteredPreviewQuestions: Array<Question> = useMemo(() => {
    const hasMoreSections: boolean = Boolean(
      (previewSections?.length ?? 0) > 1,
    );

    return hasMoreSections && preferences?.hide_document_uploads_within_sections
      ? removeGivenQuestionType(previewQuestions, 'upload')
      : previewQuestions;
  }, [
    previewQuestions,
    previewSections?.length,
    preferences?.hide_document_uploads_within_sections,
  ]);

  const filteredPreviewSectionQuestions: Array<Question> = useMemo(() => {
    const hasMoreSections = Boolean((previewSections?.length ?? 0) > 1);
    return hasMoreSections && preferences?.hide_document_uploads_within_sections
      ? removeGivenQuestionType(previewSectionQuestions, 'upload')
      : previewSectionQuestions;
  }, [
    previewSectionQuestions,
    previewSections?.length,
    preferences?.hide_document_uploads_within_sections,
  ]);

  const updatePreviewQuestion = async ({
    questionId,
    answer,
    answerBy,
  }: {
    questionId: number;
    answer: string;
    answerBy?: string;
  }) => {
    const newSelectedQuestions: Array<Question> = (
      previewQuestionnaire?.questionnaire_questions || []
    )?.map((q: Question) =>
      q.id === questionId ? { ...q, answer, answer_by: answerBy || null } : q,
    );

    const newSelectedSections: Array<Section> = previewSections.map(
      (section) => {
        const updatedQuestionnaireQuestions: Array<Question> =
          section.questionnaire_questions?.map((q) => {
            return q.id === questionId
              ? { ...q, answer, answer_by: answerBy || null }
              : q;
          }) || [];

        section.questionnaire_questions = updatedQuestionnaireQuestions;

        return section;
      },
    );

    try {
      setPreviewQuestionnaire({
        ...(previewQuestionnaire as Questionnaire),
        questionnaire_questions: newSelectedQuestions,
        sections: newSelectedSections,
      });

      const { error } = await adminAnswerQuestion({
        company_id: Number(companyId),
        answer,
        id: questionId,
        answer_by: answerBy,
        token,
      });

      if (error) {
        throw new Error(error);
      } else {
        addToast('success', 'Updated client answer');
      }
    } catch (error) {
      addToast('error', "We couldn't save the answer. Please try again!", {
        error,
      });
      setPreviewQuestionnaire(previousPreviewQuestionnaire);
    }
  };

  const filteredSections: Array<Section> = useMemo(
    () => getTriggeredSections(previewSections, previewQuestions),
    [previewSections, previewQuestions],
  );

  const selectedPreviewSignatures: Array<EngagementSignature> = useMemo(() => {
    const engagementSignatures: Array<EngagementSignature> =
      filteredSections?.find((s: Section) => s.id === previewSection?.id)
        ?.engagement_signatures || [];

    return engagementSignatures;
  }, [filteredSections, previewSection?.id]);

  // Align pending logic with UI when uploads are hidden within sections
  const previewSectionsForBlocking: Array<Section> = useMemo(() => {
    const hasMoreSections: boolean = Boolean(
      (previewSections?.length ?? 0) > 1,
    );
    const shouldHideUploads: boolean = Boolean(
      hasMoreSections && preferences?.hide_document_uploads_within_sections,
    );

    if (!shouldHideUploads) return previewSections;

    return (previewSections || []).map((section) => ({
      ...section,
      questionnaire_questions: (section.questionnaire_questions || []).filter(
        (q) => q.question_type !== 'upload',
      ),
    }));
  }, [previewSections, preferences?.hide_document_uploads_within_sections]);

  const {
    isQuestionnairePending,
    hasBlockedQuestion,
    hasBlockedSection,
    questionnaireBlockedQuestions,
    firstBlockedSectionId,
    firstBlockedQuestion,
  } = getBlockedSections(previewSectionsForBlocking, true);

  const goToNextIncompletePreviewSection = useCallback(() => {
    if (isQuestionnairePending) setShowIncompletePreviewNavigation(true);

    const hasMoreSections: boolean = Boolean(
      (previewSections?.length ?? 0) > 1,
    );

    if (hasMoreSections && preferences?.hide_document_uploads_within_sections) {
      const firstDocumentChecklistSection: Section | undefined =
        previewSections?.find((s: Section) => s.section_type === 10);
      const firstBlockedUploadQuestion: Question | undefined = Object.values(
        questionnaireBlockedQuestions,
      )
        ?.flatMap((q) => q)
        ?.find((q) => q?.question_type === 'upload');

      return (
        push(
          `/preview/${previewCompanyId}?questionnaireId=${questionnaireId}&sectionId=${firstDocumentChecklistSection?.id}#question${firstBlockedUploadQuestion?.id}`,
        ),
        { scroll: false }
      );
    }

    if (hasBlockedSection) {
      return (
        push(
          `/preview/${previewCompanyId}/questionnaire/${questionnaireId}?sectionId=${firstBlockedSectionId}`,
        ),
        { scroll: false }
      );
    }

    if (!hasBlockedQuestion) return;

    return (
      push(
        `/preview/${previewCompanyId}/questionnaire/${questionnaireId}?sectionId=${firstBlockedQuestion?.section_id}#question${firstBlockedQuestion?.id}`,
      ),
      { scroll: false }
    );
  }, [
    firstBlockedQuestion,
    firstBlockedSectionId,
    hasBlockedQuestion,
    hasBlockedSection,
    isQuestionnairePending,
    preferences?.hide_document_uploads_within_sections,
    previewCompanyId,
    previewSections,
    push,
    questionnaireBlockedQuestions,
    questionnaireId,
  ]);

  const loadPreviewAnnotations = useCallback(async () => {
    if (typeof previewExtractedDocumentId !== 'number') return;
    if (typeof previewCompanyIdForAnnotations !== 'number') return;

    try {
      const annotations = await getAnnotations({
        token,
        extractedDocumentId: previewExtractedDocumentId,
        companyId: previewCompanyIdForAnnotations,
      });

      if (Array.isArray(annotations)) {
        setPreviewAnnotations(annotations);
      }
    } catch (error) {
      addToast('error', null, { error });
    }
  }, [
    previewExtractedDocumentId,
    previewCompanyIdForAnnotations,
    addToast,
    token,
  ]);

  const updatePreviewParentQuestion = (question: Question) => {
    const updatedQuestions = previewQuestions.map((q) => {
      if (q.id === question.id) {
        return question;
      }
      return q;
    });
    setPreviewQuestionnaire({
      ...(previewQuestionnaire as Questionnaire),
      questionnaire_questions: updatedQuestions,
    });
  };

  useEffect(() => {
    if (typeof previewExtractedDocumentId !== 'number') return;
    if (typeof previewCompanyIdForAnnotations !== 'number') return;

    loadPreviewAnnotations();
  }, [
    previewExtractedDocumentId,
    previewCompanyIdForAnnotations,
    loadPreviewAnnotations,
  ]);

  return (
    <PreviewContext
      value={{
        color,
        currentPage,
        dimensions,
        goToNextIncompletePreviewSection,
        isPreviewQuestionnairePending: isQuestionnairePending,
        loadPreviewQuestionnaire,
        getDocumentsUploadedOutside,
        mode,
        pageRotations,
        previewAnnotations,
        previewCompanyId,
        previewCompanyIdForAnnotations,
        previewDocumentCreatedAt,
        previewDocumentStatus,
        previewExtractedDocumentId,
        previewFile,
        previewFileOriginalLink,
        previewQuestionnaire,
        previewQuestions: filteredPreviewQuestions,
        previewSection,
        previewSectionQuestions: filteredPreviewSectionQuestions,
        previewSections: filteredSections,
        rotationDegrees,
        selectedIds,
        selectedPreviewQuestion,
        selectedPreviewSignatures,
        selectedTaxReturn,
        setCurrentPage,
        setSelectedTaxReturn,
        setColor,
        setDimensions,
        setMode,
        setPageRotations,
        setPreviewAnnotations,
        setPreviewCompanyIdForAnnotations,
        setPreviewDocumentCreatedAt,
        setPreviewDocumentStatus,
        setPreviewExtractedDocumentId,
        setPreviewFile: setPreviewFileOrDownload,
        setPreviewFileOriginalLink,
        setPreviewQuestionnaire,
        setRotationDegrees,
        setSelectedIds,
        setSelectedPreviewQuestion,
        setShowIncompletePreviewNavigation,
        showIncompletePreviewNavigation,
        updatePreviewQuestion,
        updatePreviewParentQuestion,
      }}
    >
      {children}
      <PreviewDialog />
    </PreviewContext>
  );
};

export { PreviewContext, PreviewProvider };
